import {
  DEBOUNCE_HISTORY_VERSION_TIME_PERIOD,
  FeedbackTab,
} from '@chegg-wt/core/constants/paraphraser'
import { Step } from '@tiptap/pm/transform'
import { Dispatch } from 'redux'
import {
  adjustSentenceOffsets,
  appendFeedback,
  deleteFeedback,
  setActiveFeedbackCardId,
  setActiveFeedbackTab,
  setFeedbacks,
  setFetchNewSentencesLoading,
} from '@chegg-wt/core/state/paraphraser/actions'
import {
  activeFeedbackTabSelector,
  feedbacksSelector,
  paperIdSelector,
  paperStatusSelector,
  rewritePanelSentencesSelector,
  sentenceOffsetsSelector,
} from '@chegg-wt/core/state/paraphraser/selectors'
import { Feedback, FeedbackType } from '@chegg-wt/core/types/paraphraser'
import { AppState } from '@chegg-wt/core/state'
import {
  adjustFeedbacksInRedux,
  setNextCardActiveAfterDeleteOrUpdate,
} from '@chegg-wt/core/components/ProofReader/CardsContainer/SuggestionTabs/utils'
import { CheckTypeInput } from '@chegg-wt/core/globalTypesOneGraph'
import {
  SentenceOffset,
  WritingAssistantPaperStatus,
} from '@chegg-wt/core/state/paraphraser/types'
import { onCloseQARightPanel } from '@chegg-wt/core/components/ProofReader/QuickActionRightPanel/functions/utils'
import { getFeedbackPosition } from '../helpers'
import { IS_HISTORY_CHANGE } from '../constants'
import getFeatureEditor from '../getEditor'
import { newFeedbacksOnEditHandler } from '../pusherDataHandlers'
import pusherManager from '../pusherManager'
import { handleManualEdits } from '../manualEditHelpers'
import {
  HistoryCurrentVersion,
  HistoryDeltaAction,
  HistoryRemoveGrammarCards,
  HistoryRemoveParaphraseCards,
  HistoryVersion,
  RedoFeedbackDeltaParams,
  SentencesDelta,
  UndoFeedbackDeltaParams,
} from './types'
import history from './history'

export const debounceHelper = (func: Function, delay: number) => {
  let timeout
  return (callNow: boolean = false, ...args) => {
    const after = () => {
      timeout = null
      func(...args)
    }
    if (timeout) clearTimeout(timeout)
    if (callNow) timeout = setTimeout(after, 0)
    // To trigger function after all asynchronous operations
    else timeout = setTimeout(after, delay)
  }
}

export const debounceOnCommitHistory = debounceHelper(
  () => history.commit(),
  DEBOUNCE_HISTORY_VERSION_TIME_PERIOD
)

export const mergeSteps = (steps: Step[]): Step[] => {
  if (steps.length < 2) return steps
  const merged = steps.reduce((mergedSteps, step) => {
    const lastStep = mergedSteps[mergedSteps.length - 1]
    const mergedStep = lastStep ? lastStep.merge(step) : null
    if (mergedStep) {
      /* eslint-disable no-param-reassign */
      mergedSteps[mergedSteps.length - 1] = mergedStep
    } else {
      mergedSteps.push(step)
    }
    return mergedSteps
  }, [])
  return merged
}

const filterEditorMarks = (json: ReturnType<Step['toJSON']>) => {
  // Remove the underline and highlight from marks in history
  const excludedMarks = ['customunderline', 'customhighlight']
  json?.slice?.content?.forEach((node) => {
    if (node.type === 'text' && node.marks) {
      node.marks = node.marks.filter((mark) => !excludedMarks.includes(mark.type))
    }
  })
}

export const closeQuickActionRighPanelIfOpen = (state: AppState, dispatch: Dispatch) => {
  const rewritePanelSentences = rewritePanelSentencesSelector(state)
  if (rewritePanelSentences.length > 0) {
    onCloseQARightPanel(dispatch)
  }
}

export const getHistoryVersionWithStepInJson = (
  currentData: HistoryCurrentVersion
): HistoryVersion => {
  const stepsJson = currentData.steps.map((step) => {
    const json = step.toJSON()
    filterEditorMarks(json) // Modify this json object
    return json
  })

  const { steps, ...historyData } = {
    ...currentData,
    stepsJson,
  }
  return historyData
}

export const applyStepsAndGetInversion = (stepsJson: JSON[]): JSON[] => {
  const editor = getFeatureEditor()
  const invertedSteps = stepsJson.map((stepJson) => {
    const { state, view } = editor
    const oldDoc = state.doc
    const step = Step.fromJSON(state.schema, stepJson)
    const transaction = state.tr.step(step).setMeta(IS_HISTORY_CHANGE, true)
    view.dispatch(transaction)

    // Set the cursor at the end of the document after the step
    editor
      .chain()
      .focus()
      .setTextSelection(transaction.mapping.map((stepJson as any).to))
      .scrollIntoView()
      .run()
    const inverted = step.invert(oldDoc).toJSON()
    filterEditorMarks(inverted)
    return inverted
  })
  return invertedSteps
}

const isSameTabOpen = (
  feedbackType: FeedbackType,
  activeFeedbackTab: FeedbackTab
): boolean => {
  return (
    (feedbackType === FeedbackType.GRAMMAR &&
      activeFeedbackTab === FeedbackTab.GRAMMAR_TAB) ||
    (feedbackType === FeedbackType.PARAPHRASE &&
      activeFeedbackTab === FeedbackTab.PARAPHRASE_TAB)
  )
}

const openSameTab = (
  feedbackType: FeedbackType,
  activeFeedbackTab: FeedbackTab,
  dispatch: Dispatch
) => {
  if (!isSameTabOpen(feedbackType, activeFeedbackTab)) {
    dispatch(
      setActiveFeedbackTab(
        feedbackType === FeedbackType.GRAMMAR
          ? FeedbackTab.GRAMMAR_TAB
          : FeedbackTab.PARAPHRASE_TAB
      )
    )
  }
}

const selectNextCard = (
  feedbacks: Feedback[],
  removedFeedback: Feedback,
  dispatch: Dispatch
) => {
  const filteredFeedbacks = feedbacks.filter(
    (feedback) => feedback.feedbackType === removedFeedback.feedbackType
  )
  const removedIndex = filteredFeedbacks.indexOf(removedFeedback)
  setNextCardActiveAfterDeleteOrUpdate(removedIndex, filteredFeedbacks, (id) =>
    dispatch(setActiveFeedbackCardId(id))
  )
}

export const getFeedbackKeyFromPosition = (position: Feedback['position']): string => {
  return `${position.offset}_${position.length}`
}
export const getKeyFromSentence = (id: string, sentence: string): string => {
  return `${id}_${sentence}`
}

export const undoFeedbackDelta = ({
  feedbackDelta,
  dispatch,
  state,
  removedGrammarCards,
  removedParaphraseCards,
}: UndoFeedbackDeltaParams) => {
  const editor = getFeatureEditor()
  feedbackDelta.forEach((delta) => {
    const { feedback, action } = delta
    const activeFeedbackTab = activeFeedbackTabSelector(state)
    const sentenceOffsets = sentenceOffsetsSelector(state)
    const feedbackPosition = getFeedbackPosition(feedback, sentenceOffsets)
    editor.commands.clearAllHighlights()
    if (
      action === HistoryDeltaAction.APPLY &&
      feedback.feedbackType === FeedbackType.GRAMMAR
    ) {
      // We need to adjust grammar offsets in this case
      const feedbacks = feedbacksSelector(state)
      const appliedFeedback = {
        ...feedback,
        impactedText: feedback.suggestedText,
      }
      appliedFeedback.suggestedText = ' '.repeat(appliedFeedback.position.length) // Only length is used in adjustFeedbacksInRedux, text not matter
      adjustFeedbacksInRedux(feedbacks, appliedFeedback, dispatch)
    }
    dispatch(appendFeedback([feedback]))
    openSameTab(feedback.feedbackType, activeFeedbackTab, dispatch)
    const endCursorPosition = feedbackPosition.offset + feedbackPosition.length
    editor
      .chain()
      .setCustomUnderline({
        id: feedback.id,
        offset: feedbackPosition.offset,
        length: feedbackPosition.length,
        type: feedback.feedbackType,
      })
      .setTextSelection({ from: endCursorPosition, to: endCursorPosition })
      .scrollIntoView()
      .run()
    dispatch(setActiveFeedbackCardId(feedback.id))

    if (action === HistoryDeltaAction.DISCARD) {
      // Special case to handle discard
      const sentence = sentenceOffsets.find(
        (sentence) => sentence.id === feedback.sentenceId
      )
      const key = getKeyFromSentence(feedback.sentenceId, sentence.originalText)
      if (feedback.feedbackType === FeedbackType.GRAMMAR) {
        const { position } = feedback
        removedGrammarCards.get(key).delete(getFeedbackKeyFromPosition(position))
        if (!removedGrammarCards.get(key).size) removedGrammarCards.delete(key)
      } else removedParaphraseCards.delete(key)
    }
  })
}

export const redoFeedbackDelta = ({
  feedbackDelta,
  dispatch,
  state,
  removedGrammarCards,
  removedParaphraseCards,
}: RedoFeedbackDeltaParams) => {
  const editor = getFeatureEditor()
  feedbackDelta.forEach((delta) => {
    const { feedback, action } = delta
    editor.commands.clearAllHighlights()
    const activeFeedbackTab = activeFeedbackTabSelector(state)
    const sentenceOffsets = sentenceOffsetsSelector(state)
    if (
      action === HistoryDeltaAction.APPLY &&
      feedback.feedbackType === FeedbackType.GRAMMAR
    ) {
      // We need to adjust grammar offsets in this case
      const feedbacks = feedbacksSelector(state)
      const appliedFeedback = {
        ...feedback,
        impactedText: ' '.repeat(feedback.position.length), // Only length is used in adjustFeedbacksInRedux, text not matter
      }
      adjustFeedbacksInRedux(feedbacks, appliedFeedback, dispatch)
    }
    dispatch(deleteFeedback(feedback.id))
    if (isSameTabOpen(feedback.feedbackType, activeFeedbackTab)) {
      // Remove underline if we are on same tab
      const feedbackPosition = getFeedbackPosition(feedback, sentenceOffsets)
      editor.commands.removeCustomUnderline({
        id: feedback.id,
        offset: feedbackPosition.offset,
        length: feedbackPosition.length,
      })
    }
    openSameTab(feedback.feedbackType, activeFeedbackTab, dispatch)
    const feedbacks = feedbacksSelector(state)
    selectNextCard(feedbacks, feedback, dispatch)

    if (action === HistoryDeltaAction.DISCARD) {
      // Special case to handle discard
      const sentence = sentenceOffsets.find(
        (sentence) => sentence.id === feedback.sentenceId
      )
      const key = getKeyFromSentence(feedback.sentenceId, sentence.originalText)
      if (feedback.feedbackType === FeedbackType.GRAMMAR) {
        if (!removedGrammarCards.get(key)) {
          removedGrammarCards.set(key, new Set<string>())
        }
        const { position } = feedback
        removedGrammarCards.get(key).add(getFeedbackKeyFromPosition(position))
      } else removedParaphraseCards.set(key, true)
    }
  })
}

export const removeOldFeedbacks = (
  grammarSentenceIds: Set<String>,
  paraphraseSentenceIds: Set<String>,
  state: AppState,
  dispatch: Dispatch
) => {
  if (grammarSentenceIds.size === 0 && paraphraseSentenceIds.size === 0) {
    // Optimization when we have no sentenceId to remove
    return
  }
  const editor = getFeatureEditor()
  const activeFeedbackTab = activeFeedbackTabSelector(state)
  const sentenceOffsets = sentenceOffsetsSelector(state)
  const feedbacks = feedbacksSelector(state)
  const filteredFeedbacks = feedbacks.filter((feedback) => {
    return !(
      (feedback.feedbackType === FeedbackType.GRAMMAR &&
        grammarSentenceIds.has(feedback.sentenceId)) ||
      (feedback.feedbackType === FeedbackType.PARAPHRASE &&
        paraphraseSentenceIds.has(feedback.sentenceId))
    )
  })
  const removedIds =
    activeFeedbackTab === FeedbackTab.GRAMMAR_TAB
      ? grammarSentenceIds
      : paraphraseSentenceIds
  // Remove underline for open tab
  dispatch(setFeedbacks(filteredFeedbacks))
  editor.commands.forEach(Array.from(removedIds), (removedId, { chain }) => {
    const { id, offset, length } =
      sentenceOffsets.find((sentence) => sentence.id === removedId) || {}
    if (!id) return true
    return chain()
      .removeCustomHighlight({ id, offset, length })
      .removeCustomUnderline({ id, offset, length })
      .run()
  })
}

export const addTheNewFeedbacks = async (
  grammarSentenceIds: Set<String>,
  paraphraseSentenceIds: Set<String>,
  state: AppState,
  dispatch: Dispatch
) => {
  if (grammarSentenceIds.size === 0 && paraphraseSentenceIds.size === 0) {
    // Optimization when we have no sentenceId to add
    return
  }
  const isDraftPaper = paperStatusSelector(state) === WritingAssistantPaperStatus.DRAFT
  // We will not fetch sentences in draft state
  if (isDraftPaper) return
  const initiatedAt = new Date().toISOString()
  const paperId = paperIdSelector(state)
  const sentenceOffsets = sentenceOffsetsSelector(state)
  try {
    await newFeedbacksOnEditHandler.subscribeChannels(
      dispatch,
      grammarSentenceIds.size > 0 ? FeedbackType.GRAMMAR : FeedbackType.PARAPHRASE // This is pass just to select error message
    )
  } catch {
    // Do not precess sentences if not able to make pusher connection
    return
  }
  const traceId = await pusherManager.getTraceId()
  let setLoading = false

  const grammarSentences = sentenceOffsets
    .filter((sentence) => grammarSentenceIds.has(sentence.id))
    .map((sentence) => ({
      id: sentence.id,
      originalText: sentence.originalText,
    }))
  if (grammarSentences.length > 0) {
    const isSentencesAddedToGraphlCall = newFeedbacksOnEditHandler.addWaitAndMakeSentenceCheckCall(
      {
        sentences: grammarSentences,
        paperId,
        traceId,
        initiatedAt,
        checkType: CheckTypeInput.GRAMMAR,
        dispatch,
      }
    )
    if (isSentencesAddedToGraphlCall) setLoading = true
  }
  const paraphraseSentences = sentenceOffsets
    .filter((sentence) => paraphraseSentenceIds.has(sentence.id))
    .map((sentence) => ({
      id: sentence.id,
      originalText: sentence.originalText,
    }))
  if (paraphraseSentences.length > 0) {
    const isSentencesAddedToGraphlCall = newFeedbacksOnEditHandler.addWaitAndMakeSentenceCheckCall(
      {
        sentences: paraphraseSentences,
        paperId,
        traceId,
        initiatedAt,
        checkType: CheckTypeInput.PARAPHRASE,
        dispatch,
      }
    )
    if (isSentencesAddedToGraphlCall) setLoading = true
  }
  if (setLoading) dispatch(setFetchNewSentencesLoading(true))
}

export const addDiscardedFeedbacksForFiltering = (
  sentenceOffsets: SentenceOffset[],
  feedback: Feedback,
  removedGrammarCards: HistoryRemoveGrammarCards,
  removedParaphraseCards: HistoryRemoveParaphraseCards
) => {
  const sentence = sentenceOffsets.find((sentence) => sentence.id === feedback.sentenceId)
  const key = getKeyFromSentence(feedback.sentenceId, sentence.originalText)
  if (feedback.feedbackType === FeedbackType.GRAMMAR) {
    if (!removedGrammarCards.get(key)) {
      removedGrammarCards.set(key, new Set<string>())
    }
    const { position } = feedback
    removedGrammarCards.get(key).add(getFeedbackKeyFromPosition(position))
  } else removedParaphraseCards.set(key, true)
}

const filterSentenceOffsets = (
  sentenceOffsets: SentenceOffset[],
  sentencesToBeRemoved: SentencesDelta['removedSentences']
): SentenceOffset[] => {
  const filteredSentenceOffsets = []
  let removeSentenceIndex = 0

  sentenceOffsets.forEach((sentenceOffset) => {
    while (
      removeSentenceIndex < sentencesToBeRemoved.length &&
      sentencesToBeRemoved[removeSentenceIndex].offset < sentenceOffset.offset
    ) {
      removeSentenceIndex += 1
    }

    if (
      removeSentenceIndex < sentencesToBeRemoved.length &&
      sentenceOffset.id === sentencesToBeRemoved[removeSentenceIndex].id
    ) {
      return
    }

    filteredSentenceOffsets.push(sentenceOffset)
  })

  return filteredSentenceOffsets
}

const addNewSentenceOffsets = (
  sentenceOffsets: SentenceOffset[],
  sentencesToBeAdded: SentencesDelta['addedSentences'],
  editorText: string
): SentenceOffset[] => {
  const updatedOffsets = []
  let addedSentenceIndex = 0
  let sentenceOffsetIndex = 0
  const timestamp = new Date().getTime()
  while (
    sentenceOffsetIndex < sentenceOffsets.length &&
    addedSentenceIndex < sentencesToBeAdded.length
  ) {
    if (
      sentenceOffsets[sentenceOffsetIndex].offset <
      sentencesToBeAdded[addedSentenceIndex].offset
    ) {
      updatedOffsets.push(sentenceOffsets[sentenceOffsetIndex])
      sentenceOffsetIndex += 1
    } else {
      const { offset, length } = sentencesToBeAdded[addedSentenceIndex]
      updatedOffsets.push({
        ...sentencesToBeAdded[addedSentenceIndex],
        originalText: editorText.substring(offset - 1, offset + length - 1), // offset is 1 based
        lastUpdated: timestamp,
      })
      addedSentenceIndex += 1
    }
  }
  while (sentenceOffsetIndex < sentenceOffsets.length) {
    updatedOffsets.push(sentenceOffsets[sentenceOffsetIndex])
    sentenceOffsetIndex += 1
  }
  while (addedSentenceIndex < sentencesToBeAdded.length) {
    const { offset, length } = sentencesToBeAdded[addedSentenceIndex]
    updatedOffsets.push({
      ...sentencesToBeAdded[addedSentenceIndex],
      originalText: editorText.substring(offset - 1, offset + length - 1), // offset is 1 based
      lastUpdated: timestamp,
    })
    addedSentenceIndex += 1
  }
  return updatedOffsets
}

export const applySentenceChange = (
  sentenceDelta: SentencesDelta,
  getState: () => AppState,
  dispatch: Dispatch,
  isUndo: boolean
) => {
  if (!sentenceDelta) return

  const state = getState()
  const sentenceOffsets = sentenceOffsetsSelector(state)
  const isDraftPaper = paperStatusSelector(state) === WritingAssistantPaperStatus.DRAFT
  const feedbacks = feedbacksSelector(state)
  const editor = getFeatureEditor()
  const { addedSentences, removedSentences } = sentenceDelta

  const sentencesToBeRemoved = isUndo ? addedSentences : removedSentences
  const sentencesToBeAdded = isUndo ? removedSentences : addedSentences

  // Remove the added sentneces and it's feedbacks in non draft state
  if (!isDraftPaper) {
    handleManualEdits(
      sentencesToBeRemoved,
      sentenceOffsets, // Passing old setneces not updated one
      feedbacks,
      dispatch,
      editor,
      null
    )
  }
  // Not using .filter function as below is more optimized than that using property that sentencesToBeRemoved is sorted
  const filteredSentenceOffSets = filterSentenceOffsets(
    sentenceOffsets,
    sentencesToBeRemoved
  )
  const updatedSentences = addNewSentenceOffsets(
    filteredSentenceOffSets,
    sentencesToBeAdded,
    editor.getText()
  )

  dispatch(adjustSentenceOffsets(updatedSentences))

  if (!isDraftPaper) {
    const sentenceIds = new Set<string>()
    sentencesToBeAdded.forEach((sentence) => sentenceIds.add(sentence.id))
    addTheNewFeedbacks(
      sentenceIds,
      sentenceIds,
      getState(), // Again fetching state to get latest state with updated offsets
      dispatch
    )
  }
}

export const applySentenceOffsetOnManualChange = (
  removedSentences: SentencesDelta['removedSentences'],
  getState: () => AppState,
  dispatch: Dispatch,
  isUndo: boolean
) => {
  if (removedSentences.length === 0) return
  const state = getState()
  const sentenceOffsets = sentenceOffsetsSelector(state)
  const editor = getFeatureEditor()
  const updatedSentences = isUndo
    ? addNewSentenceOffsets(sentenceOffsets, removedSentences, editor.getText())
    : filterSentenceOffsets(sentenceOffsets, removedSentences)

  dispatch(adjustSentenceOffsets(updatedSentences))
}
